import { Button, Checkbox, Modal } from "flowbite-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  HiOutlineArrowDown,
  HiOutlineCheckCircle,
  HiOutlineDocumentText,
  HiOutlineDownload,
  HiOutlineLockClosed,
  HiOutlineShieldCheck,
} from "react-icons/hi";
import { toast } from "react-toastify";
import { useApplication } from "../../contexts/ApplicationContext";
import axiosInstance from "../../config/axiosInstance";
import { COMPANY } from "../../config/apiConfig";
import { getBrandLogoPath } from "../../utils/whiteLabel";

/* Índice do documento: alimenta o sumário lateral do modal de aceite e os
   âncoras das cláusulas. Os ids abaixo precisam existir no TermsContent.
   Há dois contratos distintos: pós-pago (faturamento mensal) e pré-pago
   (consumo de saldo), escolhidos por Company.typePayment. */
export const TERMS_SECTIONS = [
  { id: "clausula-1", number: "1ª", title: "Do objeto" },
  {
    id: "clausula-2",
    number: "2ª",
    title: "Da utilização do software e obrigações",
  },
  { id: "clausula-3", number: "3ª", title: "Dos valores, reajuste e pagamento" },
  { id: "clausula-4", number: "4ª", title: "Do reajuste" },
  {
    id: "clausula-5",
    number: "5ª",
    title: "Do sigilo, confidencialidade e segurança",
  },
  { id: "clausula-6", number: "6ª", title: "Da vigência e rescisão" },
  { id: "clausula-7", number: "7ª", title: "Das condições gerais e fiança" },
  { id: "clausula-8", number: "8ª", title: "Do foro" },
];

export const TERMS_SECTIONS_PRE = [
  { id: "clausula-1", number: "1ª", title: "Do objeto" },
  {
    id: "clausula-2",
    number: "2ª",
    title: "Do acesso e das obrigações da contratante",
  },
  {
    id: "clausula-3",
    number: "3ª",
    title: "Da modalidade pré-paga e dos créditos",
  },
  {
    id: "clausula-4",
    number: "4ª",
    title: "Da proteção de dados e uso legítimo",
  },
  {
    id: "clausula-5",
    number: "5ª",
    title: "Da suspensão e da limitação de responsabilidade",
  },
  { id: "clausula-6", number: "6ª", title: "Da vigência e do encerramento" },
  { id: "clausula-7", number: "7ª", title: "Das disposições gerais e foro" },
];

export const isPrePaidTerms = (typePayment) => typePayment === "PRE";

export const getTermsSections = (typePayment) =>
  isPrePaidTerms(typePayment) ? TERMS_SECTIONS_PRE : TERMS_SECTIONS;

const formatLongDate = (date) =>
  date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

/* Cabeçalho de cláusula: número em selo + título. As margens são explícitas
   porque o preflight do Tailwind está desligado (h2 herdaria margem do browser). */
const Clause = ({ id, number, title, children }) => (
  <section id={id} className="scroll-mt-4 break-inside-avoid">
    <h2 className="m-0 mb-3 flex items-start gap-3">
      <span className="mt-[2px] inline-flex h-6 min-w-[2.25rem] shrink-0 items-center justify-center rounded bg-[var(--primary)] px-1 text-[11px] font-bold leading-none text-white">
        {number}
      </span>
      <span className="pt-[3px] text-[12px] font-bold uppercase tracking-[0.06em] text-slate-900">
        {title}
      </span>
    </h2>
    <div className="pl-0 sm:pl-[3.25rem]">{children}</div>
  </section>
);

const ClauseList = ({ children }) => (
  <ul className="m-0 list-disc space-y-2 pl-5 marker:text-[var(--primary)]">
    {children}
  </ul>
);

const DocumentBanner = ({ title, subtitle }) => (
  <div className="mb-7 border-b-2 border-slate-900 pb-4">
    <div className="mb-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
      <span>Instrumento particular</span>
      <span>Via eletrônica</span>
    </div>
    <h1 className="m-0 text-center text-[15px] font-bold uppercase leading-snug tracking-[0.08em] text-slate-900">
      {title}
    </h1>
    {subtitle ? (
      <p className="m-0 mt-2 text-center text-[11px] italic text-slate-500">
        {subtitle}
      </p>
    ) : null}
  </div>
);

/* ===================== CONTRATO PÓS-PAGO ===================== */
const PostPaidTerms = () => (
  <>
    <DocumentBanner title="Contrato de Prestação de Serviços — Termos de Uso" />

    <div className="space-y-7">
      <p className="m-0 text-justify">
        Pelo presente instrumento particular, CONTRATANTE e CONTRATADA, têm
        pactuado entre si o presente contrato, mediante as cláusulas e condições
        a seguir expostas:
      </p>

      <Clause id="clausula-1" number="1ª" title="Do objeto">
        <ClauseList>
          <li>
            O objeto do Contrato é a prestação de serviços de disponibilização
            pela CONTRATADA à CONTRATANTE de bancos de dados, informações de
            cadastros relativos aos consumidores (pessoas físicas ou jurídicas)
            em âmbito nacional, bem como serviço de proteção ao crédito e
            congêneres.
          </li>
          <li>
            A CONTRATANTE, através do meio de acesso retro descrito, realizará
            consultas às informações cadastrais disponíveis nos Bancos de Dados,
            acessadas diretamente ou através da CONTRATADA.
          </li>
        </ClauseList>
      </Clause>

      <Clause
        id="clausula-2"
        number="2ª"
        title="Da utilização do software e obrigações dos contratantes"
      >
        <ClauseList>
          <li>
            A CONTRATANTE passará a fazer parte do quadro de CLIENTES da
            CONTRATADA a partir da assinatura deste contrato, feita
            presencialmente e/ou eletronicamente, pressupondo leitura e aceite
            de todas as cláusulas.
          </li>
          <li>
            As consultas serão feitas no site da CONTRATADA com login e senha
            fornecidos.
          </li>
          <li>
            Consultas de CNPJ ou CPF, conforme o tipo da pessoa, e para cheques
            e veículos, deverá informar os dados necessários.
          </li>
          <li>
            A concessão de crédito ou decisões com base nas informações é de
            inteira responsabilidade da CONTRATANTE.
          </li>
          <li>
            A CONTRATANTE é responsável pela guarda e sigilo de suas senhas e
            deve comunicar qualquer uso indevido.
          </li>
          <li>
            O contrato permanece válido mesmo com mudanças administrativas na
            CONTRATANTE.
          </li>
          <li>
            A CONTRATADA pode alterar sites e fontes de dados sem aviso prévio.
          </li>
          <li>
            O uso de novos produtos ou funcionalidades implica adesão automática
            no primeiro uso.
          </li>
          <li>
            É responsabilidade da CONTRATANTE acompanhar as atualizações no site
            da CONTRATADA.
          </li>
          <li>
            É vedada a divulgação, revenda ou uso indevido das informações
            obtidas.
          </li>
          <li>
            As informações devem ser usadas exclusivamente para análise de
            crédito e negócios.
          </li>
          <li>
            A CONTRATADA não se responsabiliza por perdas ou danos causados pela
            interpretação das informações.
          </li>
        </ClauseList>
      </Clause>

      <Clause
        id="clausula-3"
        number="3ª"
        title="Dos valores, reajuste e forma de pagamento"
      >
        <ClauseList>
          <li>
            Faturamento mensal via fatura enviada para o e-mail da CONTRATANTE,
            conforme ANEXO I.
          </li>
          <li>
            Se o boleto não for recebido, a CONTRATANTE deve solicitá-lo à
            CONTRATADA.
          </li>
          <li>
            Pagamentos em atraso terão juros de 1% ao mês, correção pelo IGP-M e
            multa de 2%.
          </li>
          <li>
            Dados da CONTRATANTE poderão ser incluídos em cadastros de
            inadimplência.
          </li>
          <li>
            O sistema será bloqueado após 5 dias de atraso e reativado após
            regularização.
          </li>
          <li>
            O contrato continua gerando cobranças mesmo com o sistema bloqueado.
          </li>
          <li>
            A CONTRATADA pode rescindir o contrato em caso de inadimplência.
          </li>
        </ClauseList>
      </Clause>

      <Clause id="clausula-4" number="4ª" title="Do reajuste">
        <ClauseList>
          <li>
            Reajuste anual em junho com base no IGP-M ou outros índices
            permitidos por lei.
          </li>
          <li>
            A CONTRATANTE é responsável por todos os valores gerados por seu uso
            do sistema.
          </li>
        </ClauseList>
      </Clause>

      <Clause
        id="clausula-5"
        number="5ª"
        title="Do sigilo, confidencialidade e segurança das informações"
      >
        <ClauseList>
          <li>
            Login e senha são pessoais, intransferíveis e considerados
            assinatura eletrônica.
          </li>
          <li>
            A CONTRATANTE deve manter o sigilo de seus acessos e proteger seu
            ambiente de rede.
          </li>
          <li>
            A CONTRATANTE confirma o recebimento da senha e treinamento no ato
            da contratação.
          </li>
          <li>
            É obrigatória a adoção de práticas de segurança como antivírus,
            firewall e antispyware.
          </li>
          <li>As partes se comprometem a cumprir a Lei 13.709/18 (LGPD).</li>
        </ClauseList>
      </Clause>

      <Clause id="clausula-6" number="6ª" title="Da vigência e rescisão">
        <ClauseList>
          <li>
            Contrato com vigência mínima de 12 meses e renovação automática.
          </li>
          <li>
            Para não renovação, é necessário aviso prévio de 30 dias antes do
            fim da vigência.
          </li>
          <li>
            Rescisão antecipada implica multa de 10% da franquia vezes os meses
            restantes.
          </li>
          <li>
            Não poderá haver pendências no momento da solicitação de rescisão.
          </li>
          <li>
            Consultas realizadas no mês da rescisão serão cobradas
            integralmente.
          </li>
          <li>
            A CONTRATANTE é responsável por excluir protestos registrados por
            ela.
          </li>
        </ClauseList>
      </Clause>

      <Clause id="clausula-7" number="7ª" title="Das condições gerais e fiança">
        <ClauseList>
          <li>
            O signatário do contrato se torna fiador e responsável pelos
            compromissos assumidos.
          </li>
          <li>
            O fiador renuncia a benefícios legais de ordem e pode ter seus dados
            negativados.
          </li>
        </ClauseList>
      </Clause>

      <Clause id="clausula-8" number="8ª" title="Do foro">
        <p className="m-0 text-justify">
          Fica eleito o Foro da Comarca de São Paulo - SP para resolver
          quaisquer controvérsias.
        </p>
      </Clause>

      <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
        <p className="m-0 text-justify">
          E por estarem assim justos e contratados, conhecedores de todas as
          cláusulas do presente contrato, aceitando as condições estipuladas,
          assinam o presente contrato, sem quaisquer restrições ou dúvidas, em
          duas vias na presença de duas testemunhas.
        </p>
        <p className="m-0 mt-3 font-medium text-slate-900">
          São Paulo, {formatLongDate(new Date())}.
        </p>
      </div>
    </div>
  </>
);

/* ===================== CONTRATO PRÉ-PAGO ===================== */
const PrePaidTerms = () => (
  <>
    <DocumentBanner
      title="Contrato de Prestação de Serviços — Termos de Uso"
      subtitle="Modalidade pré-paga"
    />

    <div className="space-y-7">
      <p className="m-0 text-justify">
        Pelo presente instrumento, a empresa responsável pela plataforma,
        doravante denominada CONTRATADA, e a pessoa física ou jurídica
        identificada pelos dados cadastrais registrados eletronicamente na
        plataforma, doravante denominada CONTRATANTE, celebram o presente
        Contrato de Prestação de Serviços, mediante as condições abaixo.
      </p>

      <Clause id="clausula-1" number="1ª" title="Do objeto">
        <ClauseList>
          <li>
            A CONTRATADA disponibilizará à CONTRATANTE acesso à plataforma para
            realização de consultas cadastrais, comerciais, financeiras,
            creditícias e demais produtos disponibilizados no sistema,
            referentes a pessoas físicas ou jurídicas.
          </li>
          <li>
            As informações poderão ser provenientes de bancos de dados próprios
            ou de terceiros e estarão sujeitas à disponibilidade e atualização
            das respectivas fontes.
          </li>
        </ClauseList>
      </Clause>

      <Clause
        id="clausula-2"
        number="2ª"
        title="Do acesso e das obrigações da contratante"
      >
        <ClauseList>
          <li>
            O acesso será realizado por login e senha de uso pessoal, sendo a
            CONTRATANTE responsável pela guarda e pelo uso de suas credenciais.
          </li>
          <li>
            A CONTRATANTE compromete-se a utilizar as informações exclusivamente
            para finalidades legítimas relacionadas às suas atividades, sendo
            vedado uso ilícito, divulgação, comercialização ou compartilhamento
            não autorizado.
          </li>
          <li>
            As decisões tomadas com base nas informações consultadas, inclusive
            concessão ou negativa de crédito, são de exclusiva responsabilidade
            da CONTRATANTE. A CONTRATADA não garante aprovação de crédito nem
            resultado comercial específico.
          </li>
        </ClauseList>
      </Clause>

      <Clause
        id="clausula-3"
        number="3ª"
        title="Da modalidade pré-paga e dos créditos"
      >
        <ClauseList>
          <li>
            Os serviços serão prestados na modalidade PRÉ-PAGA. A CONTRATANTE
            deverá adquirir saldo antecipadamente, e cada consulta ou serviço
            realizado será automaticamente debitado do saldo disponível,
            conforme o preço vigente apresentado na plataforma.
          </li>
          <li>
            Sem saldo suficiente, novas consultas ficarão indisponíveis até nova
            recarga. A ausência de saldo não gera dívida, juros, multa ou
            cobrança mensal.
          </li>
          <li>
            Os preços poderão ser alterados pela CONTRATADA, valendo para cada
            operação o valor informado na plataforma no momento da utilização.
          </li>
          <li>
            Em caso comprovado de débito decorrente de falha da plataforma sem a
            efetiva entrega do serviço, o respectivo valor será restituído ao
            saldo da CONTRATANTE.
          </li>
        </ClauseList>
      </Clause>

      <Clause
        id="clausula-4"
        number="4ª"
        title="Da proteção de dados e do uso legítimo"
      >
        <ClauseList>
          <li>
            As partes comprometem-se a observar a Lei nº 13.709/2018 — Lei Geral
            de Proteção de Dados Pessoais (LGPD) e demais normas aplicáveis.
          </li>
          <li>
            A CONTRATANTE declara possuir finalidade legítima para as consultas
            realizadas e assume responsabilidade pelo tratamento e utilização
            das informações obtidas.
          </li>
          <li>
            É proibida a realização de consultas para finalidade ilícita, mera
            curiosidade, perseguição, divulgação ou comercialização não
            autorizada de dados.
          </li>
          <li>
            A CONTRATADA poderá manter registros de acessos e consultas para
            fins de segurança, auditoria, prevenção a fraudes, cumprimento de
            obrigações legais e defesa de direitos.
          </li>
        </ClauseList>
      </Clause>

      <Clause
        id="clausula-5"
        number="5ª"
        title="Da suspensão e da limitação de responsabilidade"
      >
        <ClauseList>
          <li>
            A CONTRATADA poderá suspender ou cancelar o acesso em caso de uso
            irregular ou ilícito da plataforma, fraude ou suspeita fundamentada
            de fraude, utilização indevida de dados, compartilhamento não
            autorizado de credenciais ou violação destes Termos.
          </li>
          <li>
            A CONTRATADA não responderá por indisponibilidades ou divergências
            decorrentes de bancos de dados ou fornecedores terceiros,
            ressalvadas as responsabilidades que legalmente lhe sejam
            atribuídas.
          </li>
        </ClauseList>
      </Clause>

      <Clause
        id="clausula-6"
        number="6ª"
        title="Da vigência e do encerramento"
      >
        <ClauseList>
          <li>
            Este contrato entra em vigor com o aceite eletrônico e permanecerá
            válido enquanto a conta estiver ativa.
          </li>
          <li>
            Por se tratar de modalidade pré-paga, não há consumo mínimo mensal,
            renovação obrigatória ou multa pelo simples encerramento da
            utilização, salvo condição comercial específica expressamente
            contratada.
          </li>
          <li>
            A CONTRATANTE poderá deixar de utilizar a plataforma a qualquer
            momento.
          </li>
        </ClauseList>
      </Clause>

      <Clause
        id="clausula-7"
        number="7ª"
        title="Das disposições gerais e do foro"
      >
        <ClauseList>
          <li>
            O cadastro, o aceite eletrônico, os registros de acesso, recargas e
            utilização da plataforma poderão ser utilizados como comprovação da
            relação contratual e das operações realizadas.
          </li>
          <li>
            A CONTRATADA poderá atualizar estes Termos para adequações legais,
            regulatórias ou operacionais, disponibilizando a versão vigente por
            meio eletrônico.
          </li>
          <li>
            Fica eleito o Foro da Comarca de São Paulo - SP, observadas as
            regras legais de competência, para dirimir eventuais controvérsias
            decorrentes deste contrato.
          </li>
        </ClauseList>
      </Clause>

      <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
        <p className="m-0 text-justify">
          A CONTRATANTE declara ter lido e compreendido integralmente as
          cláusulas acima, manifestando sua concordância por meio do aceite
          eletrônico registrado na plataforma.
        </p>
        <p className="m-0 mt-3 font-medium text-slate-900">
          São Paulo, {formatLongDate(new Date())}.
        </p>
      </div>
    </div>
  </>
);

/* Conteúdo do contrato: alterna entre pré-pago e pós-pago conforme o
   typePayment da empresa (o mesmo campo usado no restante do sistema). */
export const TermsContent = ({ className = "", typePayment }) => (
  <article
    className={`bg-white px-5 py-6 text-[13px] leading-relaxed text-slate-700 sm:px-10 sm:py-9 ${className}`}
  >
    {isPrePaidTerms(typePayment) ? <PrePaidTerms /> : <PostPaidTerms />}
  </article>
);

/* Exportação em PDF compartilhada pelo modal de aceite e pelo ModalContract:
   o container rolável precisa ser expandido antes da captura, senão o
   html2canvas fotografa só a área visível. */
export const exportTermsToPdf = async (
  rootElement,
  filename = "termos-de-uso.pdf"
) => {
  if (!rootElement) return;

  const scrollContainer = rootElement.matches?.("[data-terms-scroll-container]")
    ? rootElement
    : rootElement.querySelector("[data-terms-scroll-container]");

  const previousMaxHeight = scrollContainer?.style.maxHeight;
  const previousOverflowY = scrollContainer?.style.overflowY;

  try {
    const { default: html2pdf } = await import("html2pdf.js");

    if (scrollContainer) {
      scrollContainer.style.maxHeight = "none";
      scrollContainer.style.overflowY = "visible";
    }

    const options = {
      margin: [10, 10, 10, 10],
      filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
    };

    await html2pdf().set(options).from(rootElement).save();
  } finally {
    if (scrollContainer) {
      scrollContainer.style.maxHeight = previousMaxHeight || "";
      scrollContainer.style.overflowY = previousOverflowY || "";
    }
  }
};

export function ModalTerms() {
  const [openModal, setOpenModal] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasReadAll, setHasReadAll] = useState(false);
  const [activeSection, setActiveSection] = useState(TERMS_SECTIONS[0].id);

  const scrollRef = useRef(null);
  const { user, refetchUser } = useApplication();

  const company = user?.Company;
  const typePayment = company?.typePayment;
  const sections = useMemo(() => getTermsSections(typePayment), [typePayment]);
  const sectionIds = useMemo(() => sections.map((item) => item.id), [sections]);
  const brandLogo = useMemo(() => getBrandLogoPath(), []);
  const today = useMemo(() => formatLongDate(new Date()), []);

  const syncReadingState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollable = container.scrollHeight - container.clientHeight;
    const ratio = scrollable <= 4 ? 1 : container.scrollTop / scrollable;
    const clamped = Math.min(1, Math.max(0, ratio));

    setProgress(clamped);
    if (clamped >= 0.985) setHasReadAll(true);

    const containerTop = container.getBoundingClientRect().top;
    let current = sectionIds[0];
    sectionIds.forEach((id) => {
      const element = container.querySelector(`#${id}`);
      if (!element) return;
      if (element.getBoundingClientRect().top - containerTop <= 80) current = id;
    });
    setActiveSection(current);
  }, [sectionIds]);

  useEffect(() => {
    // Documento curto (ou tela grande) pode não gerar rolagem: libera o aceite.
    const timeout = setTimeout(syncReadingState, 120);
    return () => clearTimeout(timeout);
  }, [syncReadingState]);

  const scrollToSection = (id) => {
    const container = scrollRef.current;
    const element = container?.querySelector(`#${id}`);
    if (!container || !element) return;

    const offset =
      element.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      12;
    container.scrollTo({ top: offset, behavior: "smooth" });
  };

  const scrollToEnd = () => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  };

  const handleExportPdf = async () => {
    try {
      setExporting(true);
      await exportTermsToPdf(scrollRef.current, "termos-de-uso.pdf");
    } catch (error) {
      console.error("Erro ao exportar os termos em PDF:", error);
      toast.error("Não foi possível exportar o PDF. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const handleAcceptTerms = async () => {
    try {
      setLoading(true);
      await axiosInstance.post(COMPANY.ACCEPT_TERMS);
      await refetchUser();
      setOpenModal(false);
    } catch (error) {
      console.error("Erro ao aceitar termos:", error);
      toast.error("Erro ao salvar o aceite. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = Math.round(progress * 100);

  return (
    <Modal
      show={openModal}
      size="7xl"
      theme={{
        content: {
          base: "relative h-full w-full p-2 sm:p-4 md:h-auto",
          inner:
            "relative flex max-h-[94dvh] flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5",
        },
      }}
    >
      {/* ============================ CABEÇALHO ============================ */}
      <header
        className="relative flex shrink-0 items-center gap-3 overflow-hidden px-5 py-3 text-white sm:px-6"
        style={{ background: "var(--hero-gradient)" }}
      >
        <div
          className="pointer-events-none absolute -right-12 -top-20 h-48 w-48 rounded-full blur-3xl"
          style={{ background: "var(--hero-glow)" }}
        />

        {brandLogo ? (
          <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white p-1 shadow-sm">
            <img
              src={brandLogo}
              alt="Logo"
              className="max-h-full max-w-full object-contain"
            />
          </div>
        ) : (
          <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white/10 ring-1 ring-white/25">
            <HiOutlineDocumentText className="h-5 w-5" />
          </div>
        )}

        <div className="relative min-w-0 flex-1">
          <h2 className="m-0 truncate text-[15px] font-bold leading-tight">
            Termos de Uso
          </h2>
          <p className="m-0 truncate text-[11px] leading-snug text-white/60">
            Leia o documento na íntegra — o aceite é registrado eletronicamente.
          </p>
        </div>

        <span className="relative inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-400/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-200 ring-1 ring-amber-300/30">
          <HiOutlineLockClosed className="h-3.5 w-3.5" />
          Aceite obrigatório
        </span>
      </header>

      {/* ============================ CORPO ============================ */}
      <div className="flex min-h-0 flex-1">
        {/* Sumário lateral (div, não <aside>: o CSS global pinta aside > div) */}
        <div className="hidden min-h-0 w-[268px] shrink-0 flex-col border-r border-slate-200 bg-slate-50/70 lg:flex">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              <span>Progresso da leitura</span>
              <span className="text-[var(--primary)]">{progressPercent}%</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-[var(--primary)] transition-[width] duration-200"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-500">
              {hasReadAll ? (
                <>
                  <HiOutlineCheckCircle className="h-4 w-4 text-emerald-600" />
                  <span className="text-emerald-700">Leitura concluída</span>
                </>
              ) : (
                <>
                  <HiOutlineArrowDown className="h-4 w-4" />
                  <span>Role até o fim para liberar o aceite</span>
                </>
              )}
            </div>
          </div>

          <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
            <div className="px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
              Cláusulas do contrato
            </div>
            <ul className="m-0 list-none space-y-0.5 p-0">
              {sections.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(item.id)}
                      className={`flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-[12px] leading-snug transition-colors ${
                        isActive
                          ? "bg-white font-semibold text-[var(--primary)] shadow-sm ring-1 ring-slate-200"
                          : "text-slate-600 hover:bg-white hover:text-slate-900"
                      }`}
                    >
                      <span
                        className={`mt-[1px] w-6 shrink-0 text-[11px] font-bold ${
                          isActive ? "text-[var(--primary)]" : "text-slate-400"
                        }`}
                      >
                        {item.number}
                      </span>
                      <span className="min-w-0 flex-1">{item.title}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="border-t border-slate-200 px-5 py-4">
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-[12px] font-semibold text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <HiOutlineDownload className="h-4 w-4" />
              {exporting ? "Gerando PDF..." : "Baixar cópia em PDF"}
            </button>
            <p className="m-0 mt-3 flex items-start gap-1.5 text-[11px] leading-snug text-slate-500">
              <HiOutlineShieldCheck className="mt-[1px] h-4 w-4 shrink-0 text-slate-400" />
              Conteúdo protegido e em conformidade com a Lei 13.709/18 (LGPD).
            </p>
          </div>
        </div>

        {/* Documento */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div className="h-1 w-full shrink-0 bg-slate-200 lg:hidden">
            <div
              className="h-full bg-[var(--primary)] transition-[width] duration-200"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div
            ref={scrollRef}
            data-terms-scroll-container
            onScroll={syncReadingState}
            className="relative min-h-0 flex-1 overflow-y-auto bg-slate-100/60 px-3 py-4 sm:px-6 sm:py-6"
          >
            <div className="mx-auto max-w-3xl rounded-lg border border-slate-200 bg-white shadow-sm">
              <TermsContent typePayment={typePayment} />
            </div>
          </div>

          {!hasReadAll && (
            <button
              type="button"
              onClick={scrollToEnd}
              className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-slate-900/85 px-4 py-2 text-[12px] font-semibold text-white shadow-lg backdrop-blur transition-colors hover:bg-slate-900"
            >
              <HiOutlineArrowDown className="h-4 w-4" />
              Ir para o fim
            </button>
          )}
        </div>
      </div>

      {/* ============================ RODAPÉ / ACEITE ============================ */}
      <footer className="shrink-0 border-t border-slate-200 bg-white px-5 py-4 sm:px-8">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <label
            htmlFor="accept"
            className={`flex flex-1 items-start gap-3 rounded-lg border p-3 transition-colors ${
              hasReadAll
                ? "cursor-pointer border-slate-300 bg-slate-50 hover:border-[var(--primary)]"
                : "cursor-not-allowed border-slate-200 bg-slate-50/60 opacity-70"
            }`}
          >
            <Checkbox
              id="accept"
              className="mt-[3px] cursor-pointer"
              checked={accepted}
              disabled={!hasReadAll}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold text-slate-900">
                Li e aceito integralmente os Termos de Uso
              </span>
              <span className="mt-1 block text-[11px] leading-snug text-slate-500">
                {hasReadAll
                  ? `Declaro ter poderes para representar ${
                      company?.socialReason || company?.name || "a contratante"
                    }. O aceite será registrado em ${today} e vinculado a ${
                      user?.email || "este usuário"
                    }.`
                  : "Role o documento até o final para habilitar o aceite."}
              </span>
            </span>
          </label>

          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 lg:hidden"
            >
              <HiOutlineDownload className="h-4 w-4" />
              PDF
            </button>

            <Button
              size="lg"
              className="w-full whitespace-nowrap bg-[var(--primary)] font-semibold text-white shadow-sm hover:bg-[var(--primary)] enabled:hover:brightness-110 xl:w-auto"
              onClick={handleAcceptTerms}
              disabled={!accepted || loading}
              isProcessing={loading}
            >
              {loading ? "Registrando aceite..." : "Aceitar e continuar"}
            </Button>
          </div>
        </div>
      </footer>
    </Modal>
  );
}
